import { PropsWithChildren } from 'react';

import { LoadingScreenSamFull } from '../../components/LoadingScreen/LoadingScreenSamFull.tsx';
import { useAppSelector } from '../../redux/hooks.ts';
import { PropertyDataSyncDriver } from './PropertyDataSyncDriver.tsx';
import { useAdjustmentSettingsSyncDriver } from './useAdjustmentSettingsSyncDriver.ts';
import { useAlertsSyncDriver } from './useAlertsSyncDriver.ts';
import { useCommentarySyncDriver } from './useCommentarySyncDriver.ts';
import { useCompAddSyncDriver } from './useCompAddSyncDriver.ts';
import { useCompSequenceSyncDriver } from './useCompSequenceSyncDriver.ts';
import { useDepreciationSettingsSyncDriver } from './useDepreciationSettingsSyncDriver.ts';
import { useMarketAdjustmentsSyncDriver } from './useMarketAdjustmentsSyncDriver.ts';
import { useOrderLoadSyncDriver } from './useOrderLoadSyncDriver.ts';
import { useReconciliationSyncDriver } from './useReconciliationSyncDriver.ts';

const CraftsmanOrderSyncDriverInner = ({
  loadStatus,
  children,
}: PropsWithChildren<{ loadStatus: 'success' | 'error' | 'loading' }>) => {
  if (loadStatus === 'loading') {
    return (
      <LoadingScreenSamFull
        isLoading={loadStatus === 'loading'}
        what="Order"
        wrapperProps={{ backgroundColor: 'surface1' }}
        shortLoaderProps={{ delayDurationSeconds: 0.2 }}
      />
    );
  }

  if (loadStatus === 'error') throw new Error('Failed to load order');
  return children;
};

export const CraftsmanOrderSyncDriver = ({
  orderID,
  children,
}: PropsWithChildren<{ orderID: string }>) => {
  const loadStatus = useOrderLoadSyncDriver(orderID);

  useAlertsSyncDriver(orderID);
  useCompSequenceSyncDriver(orderID);
  useCompAddSyncDriver(orderID);
  useMarketAdjustmentsSyncDriver(orderID);
  useAdjustmentSettingsSyncDriver(orderID);
  useDepreciationSettingsSyncDriver(orderID);
  useCommentarySyncDriver(orderID);
  useReconciliationSyncDriver(orderID);

  const compSequence = useAppSelector((state) => state.order.compSequence);

  return (
    <>
      <PropertyDataSyncDriver orderID={orderID} id="subject" />
      {compSequence.map((compID) => (
        <PropertyDataSyncDriver key={compID} orderID={orderID} id={compID} />
      ))}
      <CraftsmanOrderSyncDriverInner loadStatus={loadStatus}>
        {children}
      </CraftsmanOrderSyncDriverInner>
    </>
  );
};
